import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../services/api';

const PilotForm = ({ isOpen, onClose }) => {
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    organization: '',
    interestArea: '',
    additionalInfo: '',
    consent: false
  });

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (formStep === 1) {
      setFormStep(2);
    } else if (formStep === 2) {
      setFormStep(3);
    } else {
      // Check if additional info is empty and confirm with user
      if (!formData.additionalInfo.trim()) {
        const userConfirmed = window.confirm(
          "You haven't shared how you'd like to contribute to the kaupapa. Are you sure you want to proceed?\n\nClick 'OK' to submit anyway, or 'Cancel' to go back and add information."
        );
        
        if (!userConfirmed) {
          return; // Don't submit, let user add info
        }
      }

      // Submit to backend
      try {
        const additionalInfoText = formData.additionalInfo.trim() || "No additional information provided";
        
        const message = `Kaupapa Interest Registration:
Job Title: ${formData.jobTitle}
Organization: ${formData.organization}
Phone: ${formData.phone}
Area of Interest: ${formData.interestArea}
How they'd like to contribute: ${additionalInfoText}`;

        await apiService.createContactInquiry({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          inquiry_type: 'pilot',
          message: message,
          company: formData.organization
        });
        alert('Thank you for your interest in the kaupapa! We appreciate your support for this vision. Someone from our team will be in touch to discuss how we can work together.');
        onClose();
        setFormStep(1);
        setFormData({ 
          firstName: '', lastName: '', email: '', phone: '', jobTitle: '', 
          organization: '', interestArea: '', additionalInfo: '', consent: false 
        });
      } catch (error) {
        console.error('Form submission error:', error);
        alert('Error submitting form. Please check your internet connection and try again. If the problem persists, please contact us directly at larz@maorifintech.co.nz');
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[9999]" data-testid="pilot-form-modal">
      <div className="bg-gray-900 border border-white/20 max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Express Interest in the Kaupapa</h3>
            <button 
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${formStep >= 1 ? 'bg-cyan-400 text-black' : 'bg-gray-700 text-white'}`}>1</div>
              <div className={`w-6 h-1 ${formStep >= 2 ? 'bg-cyan-400' : 'bg-gray-700'}`}></div>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${formStep >= 2 ? 'bg-cyan-400 text-black' : 'bg-gray-700 text-white'}`}>2</div>
              <div className={`w-6 h-1 ${formStep >= 3 ? 'bg-cyan-400' : 'bg-gray-700'}`}></div>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${formStep >= 3 ? 'bg-cyan-400 text-black' : 'bg-gray-700 text-white'}`}>3</div>
            </div>
          </div>
          
          <form onSubmit={handleFormSubmit} className="space-y-3">
            {formStep === 1 && (
              <>
                <div className="bg-cyan-900/20 border border-cyan-400/30 p-3 mb-3 rounded">
                  <p className="text-cyan-400 text-xs font-medium">Nau mai, haere mai</p>
                  <p className="text-white/80 text-xs mt-1.5 leading-relaxed">
                    Phase 1 of <span className="text-cyan-300">Hiko</span> enrols a number of wholesale counterparties Iwi, rūnanga and
                    organisations within Aotearoa. Registering your interest tells us your
                    organisation would like a conversation about Phase 1 onboarding. It is not an
                    application for any financial service and creates no obligation.
                  </p>
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="Your last name"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="+64 21 123 4567"
                  />
                </div>
              </>
            )}

            {formStep === 2 && (
              <>
                <div>
                  <label className="block text-white text-sm mb-1.5">Your Role/Position *</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="e.g. Kaiārahi, Manager, Community Leader"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">Organization/Iwi/Hapū *</label>
                  <input
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({...formData, organization: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none"
                    placeholder="e.g. Te Arawa, Business name, Community group"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-1.5">Area of Interest *</label>
                  <select
                    value={formData.interestArea}
                    onChange={(e) => setFormData({...formData, interestArea: e.target.value})}
                    required
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white focus:border-cyan-400/50 focus:outline-none"
                  >
                    <option value="">What aspect interests you most?</option>
                    <option value="hiko-phase1-enrolment">Hiko Phase 1 counterparty enrolment</option>
                    <option value="member-disbursements">Member disbursements / pūtea distribution</option>
                    <option value="merchant-payments">Whitelisted merchant payments</option>
                    <option value="data-sovereignty">Māori data sovereignty (Te Mana Raraunga)</option>
                    <option value="wai-3564">WAI 3564 supporting the claim</option>
                    <option value="partnership-opportunities">Partnership / co-design opportunities</option>
                    <option value="general-support">General support for the kaupapa</option>
                  </select>
                </div>
              </>
            )}

            {formStep === 3 && (
              <>
                <div>
                  <label className="block text-white text-sm mb-1.5">How would you like to contribute to this kaupapa?</label>
                  <textarea
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
                    rows={6}
                    className="w-full px-3 py-2 bg-gray-800/50 border border-white/20 text-white placeholder-white/50 focus:border-cyan-400/50 focus:outline-none resize-none"
                    placeholder="Share your thoughts, ideas, or how you'd like to be involved as this concept develops. E.g. community connections, expertise you could offer, feedback on the vision, etc."
                  />
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={formData.consent}
                    onChange={(e) => setFormData({...formData, consent: e.target.checked})}
                    required
                    className="mt-1"
                  />
                  <label htmlFor="consent" className="text-white/85 text-sm">
                    I'd like to stay informed about the kaupapa and agree to be contacted about future developments *
                  </label>
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
            >
              {formStep === 1 ? 'Continue' : formStep === 2 ? 'Continue' : 'Register Interest'}
            </button>
            
            {formStep > 1 && (
              <button
                type="button"
                onClick={() => setFormStep(formStep - 1)}
                className="btn-secondary w-full"
              >
                Back
              </button>
            )}
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PilotForm;
